import { useCallback } from "react";
import { types } from "@vilocnv/allsetra-core";
import { useCurrentUser, canViewRideOnMap } from "hooks";
import { ObjectRideModeEnum } from "app/data/helpers/objectRideHelpers";

// Roles that can view all rides on map (even without coordinates)
export const PRIVILEGED_ROLES = [
  "Super Admin",
  "Admin",
  "Manager",
  "View Only"
];

/**
 * Hook that provides permission checks for ride-related actions
 */
export const useRidePermissions = () => {
  const { userEmail, currentRoleName, isDriver } = useCurrentUser();

  /**
   * Check if current user can view a specific ride on map
   * - Super Admin, Admin, Manager, View Only: Can view all rides (even without coordinates)
   * - Driver: Can only view their own rides (and only if ride has valid coordinates)
   */
  const canUserViewRideOnMap = useCallback(
    (ride: types.IObjectRide): boolean => {
      // Privileged roles can ALWAYS view rides on map
      if (PRIVILEGED_ROLES.includes(currentRoleName)) {
        return true;
      }

      // For Drivers: check if it's their own ride AND has valid coordinates
      if (isDriver) {
        const rideDriverEmail = (ride as any).driver?.email;
        const isOwnRide = userEmail === rideDriverEmail;

        // Driver can only view their own rides with valid coordinates
        if (isOwnRide) {
          return canViewRideOnMap(ride);
        }
        return false;
      }

      // Default: deny access for other roles
      return false;
    },
    [currentRoleName, userEmail, isDriver]
  );

  /**
   * Check if current user can view the real address for a specific ride
   * - Super Admin: Can see all addresses
   * - Driver: Can see addresses ONLY for their own rides
   * - Admin, Manager, View Only:
   *   - Business rides: Can see addresses
   *   - Private/Mixed rides: Cannot see addresses (shows "Address is Hidden")
   */
  const canUserViewAddressForRide = useCallback(
    (ride: types.IObjectRide): boolean => {
      // Super Admin can always see addresses
      if (currentRoleName === "Super Admin") {
        return true;
      }

      // Business rides (rideMode === 1) - everyone can see addresses
      if (ride.rideMode === ObjectRideModeEnum.BUSINESS) {
        return true;
      }

      // For Private (2) or Mixed (4) rides:
      // Only the driver of that ride can see the address
      const rideDriverEmail = (ride as any).driver?.email;
      const isOwnRide = userEmail === rideDriverEmail;

      return isOwnRide;
    },
    [currentRoleName, userEmail]
  );

  return {
    canUserViewRideOnMap,
    canUserViewAddressForRide,
    currentRoleName,
    userEmail,
    isDriver
  };
};

export default useRidePermissions;
